import { FC, useCallback } from 'react';
import { useFragment } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Paper from '@mui/material/Paper';
import TableRow from '@mui/material/TableRow';
import Table from 'components/Table';
import TableCell from 'components/TableCell';
import PackageDateRow from 'components/PackageDateRow';
import Box from '@mui/material/Box';
import AccordionController from 'components/AccordionController';
import Section from 'components/Section';
import PackageEditDatesAdd from 'components/PackageEditDatesAdd';

import { usePagination } from 'components/Pagination';
import { useAvoidSuspense, useDelete, useSkipFirstLoad } from 'lib';

import Query, {
  PackageEditDatesListRefetchQuery,
} from '__generated__/PackageEditDatesListRefetchQuery.graphql';
import { PackageEditDatesList_package$key } from '__generated__/PackageEditDatesList_package.graphql';
import { PackageEditDatesList_data$key } from '__generated__/PackageEditDatesList_data.graphql';

type Props = {
  package: PackageEditDatesList_package$key;
  data: PackageEditDatesList_data$key;
};

const PackageEditDatesList: FC<Props> = props => {
  const { variables } = usePagination();

  const [pack, datesQuery] = useAvoidSuspense<
    PackageEditDatesListRefetchQuery,
    PackageEditDatesList_package$key
  >(
    graphql`
      fragment PackageEditDatesList_package on Package
      @refetchable(queryName: "PackageEditDatesListRefetchQuery")
      @argumentDefinitions(first: { type: "Int!" }, after: { type: "String" }) {
        id
        transportType
        externalFlights

        tripDates(first: $first, after: $after) {
          __id
          edges {
            node {
              id
              ...PackageDateRow_packageDate
            }
          }

          pageInfo {
            ...Table_pageInfo
          }
        }

        ...PackageDateRow_package
        ...PackageEditDatesAdd_package
      }
    `,
    props.package,
    Query
  );

  const data = useFragment(
    graphql`
      fragment PackageEditDatesList_data on Query {
        ...PackageDateRow_data
      }
    `,
    props.data
  );

  const connectionId = pack.tripDates?.__id;

  const [deleteDate, deleting] = useDelete(connectionId);

  const { edges, pageInfo } = pack.tripDates ?? {};

  useSkipFirstLoad(
    useCallback(() => {
      datesQuery.refetch({
        id: pack.id,
        ...variables,
      });
    }, [pack.id, datesQuery, variables])
  );

  return (
    <>
      {connectionId && (
        <Box sx={{ mb: 2 }}>
          <AccordionController id="packageEditDates">
            <Section id="addDate" title="Nauja data">
              <PackageEditDatesAdd package={pack} connectionId={connectionId} />
            </Section>
          </AccordionController>
        </Box>
      )}

      <Paper sx={{ p: 2 }}>
        <Table
          pagination
          pageInfo={pageInfo ?? null}
          icon={<CalendarMonthIcon />}
          loading={datesQuery.loading || deleting}
          head={
            <TableRow>
              <TableCell />
              <TableCell center>Kelionės datos</TableCell>
              <TableCell center>Galioja iki</TableCell>
              <TableCell center>Naktys</TableCell>
              {pack.transportType === 'PLANE' && !pack.externalFlights && (
                <>
                  <TableCell center>Skrydis</TableCell>
                  <TableCell center>Skrydžio kaina</TableCell>
                </>
              )}
              <TableCell center>Viešbučio kaina</TableCell>
              <TableCell />
            </TableRow>
          }>
          {edges?.map(({ node }) => (
            <PackageDateRow
              key={node.id}
              packageDate={node}
              package={pack}
              data={data}
              onDelete={deleteDate}
            />
          ))}
        </Table>
      </Paper>
    </>
  );
};

export default PackageEditDatesList;
